import React, { useCallback, useMemo, useState } from "react";

import { Stack } from "@mui/material";

import useGetTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useGetTask.v3";
import useGetTaskActivityLog from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import useGetTaskComments from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import useGetTask from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTask";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import { TASK_COMMENTS_STATIC_QUERY_PARAMS } from "../../Tasks/common";
import TaskDrawerFooter from "../../Tasks/TaskDrawerFooter/TaskDrawerFooter";
import TaskActionButtons from "../components/TaskActionButtons";
import { FormSubmissionProvider } from "../FormEngine";
import { useFormSubmissionContext } from "../FormEngine/context";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";

import TaskDetailsWorkflowUI from "./TaskDetailsWorkflowUI";

import styles from "./TaskDetailsWorkflow.module.css";

interface TaskDetailsWorkflowEmbeddedProps {
  taskId: TaskId;
}

const TaskDetailsWorkflowEmbeddedInner: React.FC<
  TaskDetailsWorkflowEmbeddedProps
> = ({ taskId }) => {
  const auth = useAdvisorRequestAuth();

  if (!auth) {
    throw new Error(
      "Authentication required for TaskDetailsWorkflowEmbedded - TaskWorkflows"
    );
  }

  const [_isMutating, setIsMutating] = useState(false);
  const { isDirty, updateDirtyState } = useFormSubmissionContext();

  const handleCloseEditMode = useCallback((): void => {
    updateDirtyState(false);
  }, [updateDirtyState]);

  // Fetch task data using both APIs
  // TaskWorkflows API for FormEngine
  const {
    data: taskWorkflowData,
    isLoading: isTaskWorkflowLoading,
    hasError: taskWorkflowHasError,
  } = useGetTask(taskId, auth);
  // Legacy API for existing components (TaskActivity, TaskAttachments)
  const {
    data: taskDetailsV3,
    isLoading: isTaskV3Loading,
    hasError: taskV3HasError,
  } = useGetTaskV3(taskId, auth);
  const { data: taskActivities } = useGetTaskActivityLog(taskId, auth);
  const {
    data: taskComments,
    isLoading: isTaskCommentsLoading,
    hasError: taskCommentsHasError,
  } = useGetTaskComments(taskId, TASK_COMMENTS_STATIC_QUERY_PARAMS, auth);

  const taskCommentsArray = useMemo(
    () => taskComments?.values || [],
    [taskComments?.values]
  );

  const taskAttachmentsArray = useMemo(
    () => taskWorkflowData?.attachments || [],
    [taskWorkflowData?.attachments]
  );

  const isLoading =
    isTaskWorkflowLoading ||
    isTaskV3Loading ||
    isTaskCommentsLoading ||
    !taskWorkflowData;

  const hasError =
    taskWorkflowHasError || taskV3HasError || taskCommentsHasError;

  const footer = useMemo(() => {
    if (!taskId || isLoading) {
      return undefined;
    }

    return isDirty ? (
      <TaskActionButtons onCancel={handleCloseEditMode} />
    ) : (
      <TaskDrawerFooter taskId={taskId} />
    );
  }, [taskId, isDirty, isLoading, handleCloseEditMode]);

  return (
    <>
      {isLoading && (
        <div className={styles.loading}>
          <LogoLoadingStill />
        </div>
      )}

      {hasError && (
        <div className={styles.error}>
          <Text variant="body">Error loading task details</Text>
        </div>
      )}

      {taskWorkflowData && taskDetailsV3 && !isLoading && !hasError && (
        <>
          {taskWorkflowData.meta.isDeleted && (
            <div className={styles.overlay} />
          )}

          <div className={styles.embeddedContainer}>
            <TaskDetailsWorkflowUI
              taskWorkflowData={taskWorkflowData}
              taskDetailsV3={taskDetailsV3}
              taskAttachments={taskAttachmentsArray}
              taskComments={taskCommentsArray}
              taskActivities={taskActivities}
              setIsDrawerOpen={handleCloseEditMode}
              setIsMutating={setIsMutating}
              isEmbedded
            />

            <Stack direction="row" justifyContent="flex-end">
              {footer}
            </Stack>
          </div>
        </>
      )}
    </>
  );
};

const TaskDetailsWorkflowEmbedded: React.FC<
  TaskDetailsWorkflowEmbeddedProps
> = (props) => {
  return (
    <FormSubmissionProvider>
      <TaskDetailsWorkflowEmbeddedInner {...props} />
    </FormSubmissionProvider>
  );
};

export default TaskDetailsWorkflowEmbedded;
